import { Link } from "@tanstack/react-router";

import type { BlogCategory } from "../../../types";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

const chip =
  "inline-flex h-9 shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-medium outline-none transition-[background-color,border-color,color] duration-200 focus-visible:ring-2 focus-visible:ring-ring";
const idle = "border-border bg-background text-muted-foreground hover:border-foreground/20 hover:text-foreground";
const active = "border-foreground bg-foreground text-background";

const Count = ({ value, on }: { value: number; on: boolean }) => (
  <span className={cn("font-mono text-[11px] tabular-nums", on ? "text-background/60" : "text-muted-foreground/70")}>{value}</span>
);

/** The blog's shelves as a row of chips, the one being read filled in. It scrolls sideways on a phone. */
export const CategoryChips = ({
  categories,
  current,
  total,
}: {
  categories: BlogCategory[];
  /** The shelf being read; none on the front page. */
  current?: string;
  /** Every post on this blog, for the "All" chip. */
  total?: number;
}) => {
  if (categories.length === 0) return null;
  return (
    <nav aria-label={m.blog_title()} className="-mx-3 overflow-x-auto px-3 [scrollbar-width:none]">
      <ul className="flex w-max gap-2 py-1">
        <li>
          <Link to="/blog" className={cn(chip, current ? idle : active)} aria-current={current ? undefined : "page"}>
            {m.blog_filter_all()}
            {total !== undefined ? <Count value={total} on={!current} /> : null}
          </Link>
        </li>
        {categories.map((category) => {
          const on = category.slug === current;
          return (
            <li key={category.id}>
              <Link
                to="/blog/category/$categorySlug"
                params={{ categorySlug: category.slug }}
                className={cn(chip, on ? active : idle)}
                aria-current={on ? "page" : undefined}
              >
                {category.name}
                <Count value={category.postCount} on={on} />
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
